"use client";

import { useMemo } from "react";
import GenericActionBar from "@/shared/ui/actions/GenericActionBar";
import type { GenericActionBarAction } from "@/shared/ui/actions/actionBar.types";
import { useUserChoices } from "@/state/globalStore";
import { useBacklogContext } from "../contexts/BacklogContextProvider";

export default function BacklogActionBars() {
  const {
    countSelectedOrigins,
    selectedOrigins,
    resetSelectedOrigins,
  } = useUserChoices();
  const { originHandlers } = useBacklogContext();
  const {
    handleCopyRawTextInputToClipboard,
    handleSelectedOriginsUnselectClick,
    handleSelectedOriginsDeleteClick,
  } = originHandlers;
  const selectedOriginsCount = countSelectedOrigins();

  const actionBarActions = useMemo<GenericActionBarAction[]>(
    () => [
      {
        key: "copy",
        label: "Copy",
        shortLabel: "Copy",
        humanReadable: "Copy selected source material to clipboard",
        icon: "copy_clipboard",
        priority: 1,
        onSelect: async () =>
          await handleCopyRawTextInputToClipboard(selectedOrigins),
      },
      {
        key: "unselect",
        label: "Unselect",
        shortLabel: "Clear",
        humanReadable: "Unselect all selected source material",
        icon: "unselect",
        tone: "warning",
        priority: 99,
        mobileBehavior: "overflow",
        onSelect: handleSelectedOriginsUnselectClick,
      },
      {
        key: "delete",
        label: "Delete",
        shortLabel: "Delete",
        humanReadable: "Delete selected source material permanently",
        icon: "delete_permanent",
        tone: "error",
        priority: 2,
        onSelect: async () =>
          await handleSelectedOriginsDeleteClick(
            {
              origin_ids: selectedOrigins,
              delete_mode: "permanent",
            },
            () => {
              resetSelectedOrigins();
            },
          ),
      },
    ],
    [
      handleCopyRawTextInputToClipboard,
      handleSelectedOriginsDeleteClick,
      handleSelectedOriginsUnselectClick,
      resetSelectedOrigins,
      selectedOrigins,
    ],
  );

  if (selectedOriginsCount === 0) {
    return null;
  }

  return (
    <GenericActionBar
      selectedItemsCount={selectedOriginsCount}
      handlingWhatThoughts="origins"
      selectionLabel={`${selectedOriginsCount} ${
        selectedOriginsCount === 1 ? "source material" : "source materials"
      }`}
      actions={actionBarActions}
    />
  );
}
